import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import PaymentIcon from '@mui/icons-material/Payment';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';

const REGION_GROUPS = [
  {
    name: 'London',
    outcodes: [
      { code: 'BARKING AND DAGENHAM', area: 'Barking and Dagenham, London' },
      { code: 'BARNET', area: 'Barnet, London' },
      { code: 'BEXLEY', area: 'Bexley, London' },
      { code: 'BRENT', area: 'Brent, London' },
      { code: 'BROMLEY', area: 'Bromley, London' },
      { code: 'CAMDEN', area: 'Camden, London' },
      { code: 'CITY OF LONDON', area: 'City of London, London' },
      { code: 'CROYDON', area: 'Croydon, London' },
      { code: 'EALING', area: 'Ealing, London' },
      { code: 'ENFIELD', area: 'Enfield, London' },
      { code: 'GREENWICH', area: 'Greenwich, London' },
      { code: 'HACKNEY', area: 'Hackney, London' },
      { code: 'HAMMERSMITH AND FULHAM', area: 'Hammersmith and Fulham, London' },
      { code: 'HARINGEY', area: 'Haringey, London' },
      { code: 'HARROW', area: 'Harrow, London' },
      { code: 'HAVERING', area: 'Havering, London' },
      { code: 'HILLINGDON', area: 'Hillingdon, London' },
      { code: 'HOUNSLOW', area: 'Hounslow, London' },
      { code: 'ISLINGTON', area: 'Islington, London' },
      { code: 'KENSINGTON AND CHELSEA', area: 'Kensington and Chelsea, London' },
      { code: 'KINGSTON UPON THAMES', area: 'Kingston upon Thames, London' },
      { code: 'LAMBETH', area: 'Lambeth, London' },
      { code: 'LEWISHAM', area: 'Lewisham, London' },
      { code: 'MERTON', area: 'Merton, London' },
      { code: 'NEWHAM', area: 'Newham, London' },
      { code: 'REDBRIDGE', area: 'Redbridge, London' },
      { code: 'RICHMOND UPON THAMES', area: 'Richmond upon Thames, London' },
      { code: 'SOUTHWARK', area: 'Southwark, London' },
      { code: 'SUTTON', area: 'Sutton, London' },
      { code: 'TOWER HAMLETS', area: 'Tower Hamlets, London' },
      { code: 'WALTHAM FOREST', area: 'Waltham Forest, London' },
      { code: 'WANDSWORTH', area: 'Wandsworth, London' },
      { code: 'WESTMINSTER', area: 'Westminster, London' }
    ]
  },
  {
    name: 'Manchester',
    outcodes: [
      { code: 'MANCHESTER CITY CENTRE', area: 'Manchester City Centre, Manchester' },
      { code: 'SALFORD', area: 'Salford, Manchester' },
      { code: 'TRAFFORD', area: 'Trafford, Manchester' },
      { code: 'STOCKPORT', area: 'Stockport, Manchester' },
      { code: 'TAMESIDE', area: 'Tameside, Manchester' },
      { code: 'OLDHAM', area: 'Oldham, Manchester' },
      { code: 'ROCHDALE', area: 'Rochdale, Manchester' },
      { code: 'BURY', area: 'Bury, Manchester' },
      { code: 'BOLTON', area: 'Bolton, Manchester' },
      { code: 'WIGAN', area: 'Wigan, Manchester' }
    ]
  },
  {
    name: 'Birmingham',
    outcodes: [
      { code: 'BIRMINGHAM CITY CENTRE', area: 'Birmingham City Centre, Birmingham' },
      { code: 'SOLIHULL', area: 'Solihull, Birmingham' },
      { code: 'SUTTON COLDFIELD', area: 'Sutton Coldfield, Birmingham' },
      { code: 'EDGBASTON', area: 'Edgbaston, Birmingham' },
      { code: 'HARBORNE', area: 'Harborne, Birmingham' },
      { code: 'DUDLEY', area: 'Dudley, Birmingham' },
      { code: 'SANDWELL', area: 'Sandwell, Birmingham' },
      { code: 'WALSALL', area: 'Walsall, Birmingham' },
      { code: 'WOLVERHAMPTON', area: 'Wolverhampton, Birmingham' },
      { code: 'COVENTRY', area: 'Coventry, Birmingham' }
    ]
  },
  {
    name: 'Liverpool',
    outcodes: [
      { code: 'LIVERPOOL CITY CENTRE', area: 'Liverpool City Centre, Liverpool' },
      { code: 'KNOWSLEY', area: 'Knowsley, Liverpool' },
      { code: 'ST HELENS', area: 'St Helens, Liverpool' },
      { code: 'SEFTON', area: 'Sefton, Liverpool' },
      { code: 'WIRRAL', area: 'Wirral, Liverpool' }
    ]
  },
  {
    name: 'Leeds',
    outcodes: [
      { code: 'LEEDS CITY CENTRE', area: 'Leeds City Centre, Leeds' },
      { code: 'HEADINGLEY', area: 'Headingley, Leeds' },
      { code: 'HORSFORTH', area: 'Horsforth, Leeds' },
      { code: 'WETHERBY', area: 'Wetherby, Leeds' },
      { code: 'PUDSEY', area: 'Pudsey, Leeds' },
      { code: 'BRADFORD', area: 'Bradford, Leeds' },
      { code: 'WAKEFIELD', area: 'Wakefield, Leeds' }
    ]
  },
  {
    name: 'Bristol',
    outcodes: [
      { code: 'BRISTOL CITY CENTRE', area: 'Bristol City Centre, Bristol' },
      { code: 'CLIFTON', area: 'Clifton, Bristol' },
      { code: 'REDLAND', area: 'Redland, Bristol' },
      { code: 'BEDMINSTER', area: 'Bedminster, Bristol' },
      { code: 'BATH', area: 'Bath, Bristol' }
    ]
  },
  {
    name: 'Sheffield',
    outcodes: [
      { code: 'SHEFFIELD CITY CENTRE', area: 'Sheffield City Centre, Sheffield' },
      { code: 'KELHAM ISLAND', area: 'Kelham Island, Sheffield' },
      { code: 'ECCLESALL', area: 'Ecclesall, Sheffield' },
      { code: 'HILLSBOROUGH', area: 'Hillsborough, Sheffield' },
      { code: 'ROTHERHAM', area: 'Rotherham, Sheffield' }
    ]
  },
  {
    name: 'Newcastle',
    outcodes: [
      { code: 'NEWCASTLE CITY CENTRE', area: 'Newcastle City Centre, Newcastle' },
      { code: 'JESMOND', area: 'Jesmond, Newcastle' },
      { code: 'GOSFORTH', area: 'Gosforth, Newcastle' },
      { code: 'SUNDERLAND', area: 'Sunderland, Newcastle' },
      { code: 'GATESHEAD', area: 'Gateshead, Newcastle' }
    ]
  },
  {
    name: 'Nottingham',
    outcodes: [
      { code: 'NOTTINGHAM CITY CENTRE', area: 'Nottingham City Centre, Nottingham' },
      { code: 'WEST BRIDGFORD', area: 'West Bridgford, Nottingham' },
      { code: 'BEESTON', area: 'Beeston, Nottingham' },
      { code: 'DERBY', area: 'Derby, Nottingham' }
    ]
  },
  {
    name: 'Leicester',
    outcodes: [
      { code: 'LEICESTER CITY CENTRE', area: 'Leicester City Centre, Leicester' },
      { code: 'STONEYGATE', area: 'Stoneygate, Leicester' },
      { code: 'OADBY', area: 'Oadby, Leicester' },
      { code: 'LOUGHBOROUGH', area: 'Loughborough, Leicester' }
    ]
  },
  {
    name: 'Southampton',
    outcodes: [
      { code: 'SOUTHAMPTON CITY CENTRE', area: 'Southampton City Centre, Southampton' },
      { code: 'PORTSMOUTH CITY CENTRE', area: 'Portsmouth City Centre, Southampton' },
      { code: 'EASTLEIGH', area: 'Eastleigh, Southampton' },
      { code: 'FAREHAM', area: 'Fareham, Southampton' }
    ]
  },
  {
    name: 'Brighton',
    outcodes: [
      { code: 'BRIGHTON CITY CENTRE', area: 'Brighton City Centre, Brighton' },
      { code: 'HOVE', area: 'Hove, Brighton' },
      { code: 'WORTHING', area: 'Worthing, Brighton' }
    ]
  },
  {
    name: 'Oxford',
    outcodes: [
      { code: 'OXFORD CITY CENTRE', area: 'Oxford City Centre, Oxford' }
    ]
  },
  {
    name: 'Cambridge',
    outcodes: [
      { code: 'CAMBRIDGE CITY CENTRE', area: 'Cambridge City Centre, Cambridge' },
      { code: 'PETERBOROUGH', area: 'Peterborough, Cambridge' }
    ]
  },
  {
    name: 'Edinburgh',
    outcodes: [
      { code: 'EDINBURGH CITY CENTRE', area: 'Edinburgh City Centre, Edinburgh' },
      { code: 'ABERDEEN', area: 'Aberdeen, Edinburgh' },
      { code: 'DUNDEE', area: 'Dundee, Edinburgh' }
    ]
  },
  {
    name: 'Glasgow',
    outcodes: [
      { code: 'GLASGOW CITY CENTRE', area: 'Glasgow City Centre, Glasgow' }
    ]
  },
  {
    name: 'Cardiff',
    outcodes: [
      { code: 'CARDIFF CITY CENTRE', area: 'Cardiff City Centre, Cardiff' },
      { code: 'SWANSEA CITY CENTRE', area: 'Swansea City Centre, Cardiff' },
      { code: 'NEWPORT', area: 'Newport, Cardiff' }
    ]
  },
  {
    name: 'Belfast',
    outcodes: [
      { code: 'BELFAST CITY CENTRE', area: 'Belfast City Centre, Belfast' },
      { code: 'LONDONDERRY', area: 'Londonderry, Belfast' }
    ]
  }
];

const AdminUserEdit: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const showToast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab State
  const [activeTab, setActiveTab] = useState(0);

  // User States
  const [user, setUser] = useState<any>(null);
  const [details, setDetails] = useState<any>(null);

  // Active status state
  const [isActive, setIsActive] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Password States
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Instructor Custom Fields State
  const [profileOutcodes, setProfileOutcodes] = useState<string[]>([]);
  const [profileVehicleImageUrls, setProfileVehicleImageUrls] = useState<string[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [isUploadingVehicleImage, setIsUploadingVehicleImage] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Form Fields State
  const [formData, setFormData] = useState<any>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    dateOfBirth: '',
    bio: '',
    profileImageUrl: '',
    // Instructor specific
    licenseNumber: '',
    licenseExpiryDate: '',
    profileInfo: '',
    baseHourlyRate: 25.00,
    transmissionType: 'Manual',
    licenseTypes: 'B',
    logoUrl: '',
    // Student specific
    studentNumber: '',
    theoryTestPassDate: '',
    theoryTestPassReference: '',
    dvlaShareCode: '',
    eyesightTestPassed: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalConditions: '',
    licenseStatus: 'None',
    licenseRejectionReason: '',
  });

  const fetchUserDetails = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.getUserDetails(userId);
      const data = res.data;
      setUser(data.user);
      setDetails(data.details);
      setIsActive(data.user.isActive);

      // Populate form data
      const baseFields = {
        firstName: data.user.firstName || '',
        lastName: data.user.lastName || '',
        phoneNumber: data.user.phoneNumber || '',
        dateOfBirth: data.user.dateOfBirth ? data.user.dateOfBirth.split('T')[0] : '',
        bio: data.user.bio || '',
        profileImageUrl: data.user.profileImageUrl || '',
      };

      if (data.user.role === 'Instructor' && data.details) {
        setProfileOutcodes(data.details.outcodes || []);
        setProfileVehicleImageUrls(data.details.vehicleImageUrls ? data.details.vehicleImageUrls.split(';').filter(Boolean) : []);
        setLogoUrl(data.details.logoUrl || null);
        setFormData({
          ...formData,
          ...baseFields,
          licenseNumber: data.details.licenseNumber || '',
          licenseExpiryDate: data.details.licenseExpiryDate ? data.details.licenseExpiryDate.split('T')[0] : '',
          profileInfo: data.details.profileInfo || '',
          baseHourlyRate: data.details.baseHourlyRate || 25.00,
          transmissionType: data.details.transmissionType || 'Manual',
          licenseTypes: data.details.licenseTypes || 'B',
          logoUrl: data.details.logoUrl || '',
        });
      } else if (data.user.role === 'Student' && data.details) {
        setFormData({
          ...formData,
          ...baseFields,
          studentNumber: data.details.studentNumber || '',
          licenseNumber: data.details.licenseNumber || '',
          licenseExpiryDate: data.details.licenseExpiryDate ? data.details.licenseExpiryDate.split('T')[0] : '',
          theoryTestPassDate: data.details.theoryTestPassDate ? data.details.theoryTestPassDate.split('T')[0] : '',
          theoryTestPassReference: data.details.theoryTestPassReference || '',
          dvlaShareCode: data.details.dvlaShareCode || '',
          eyesightTestPassed: data.details.eyesightTestPassed || false,
          emergencyContactName: data.details.emergencyContactName || '',
          emergencyContactPhone: data.details.emergencyContactPhone || '',
          medicalConditions: data.details.medicalConditions || '',
          licenseStatus: data.details.licenseStatus || 'None',
          licenseRejectionReason: data.details.licenseRejectionReason || '',
          transmissionType: data.details.transmissionType || 'Manual',
        });
      } else {
        setFormData({
          ...formData,
          ...baseFields,
        });
      }
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      setError('An error occurred while loading user details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev: any) => ({ ...prev, [name]: checked }));
  };

  const handleToggleStatus = async () => {
    if (!userId) return;
    const action = isActive ? 'deactivate' : 'activate';
    const isConfirmed = await confirm({
      title: `${isActive ? 'Deactivate' : 'Activate'} User Account`,
      message: `Are you sure you want to ${action} this user's account? The user will ${isActive ? 'no longer' : 'again'} be able to log in.`,
      severity: 'warning',
    });
    if (!isConfirmed) return;

    try {
      setStatusUpdating(true);
      if (isActive) {
        await api.deactivateUser(userId);
      } else {
        await api.activateUser(userId);
      }
      setIsActive(!isActive);
      showToast(`User account ${isActive ? 'deactivated' : 'activated'} successfully.`, 'success');
    } catch (err: any) {
      console.error('Error toggling status:', err);
      showToast('An error occurred while changing account status.', 'error');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Upload/delete handlers for Logo & Vehicle Photos & Served Regions
  const handleVehicleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    if (profileVehicleImageUrls.length >= 3) {
      showToast('You can upload a maximum of 3 vehicle photos.', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB.', 'error');
      return;
    }

    try {
      setIsUploadingVehicleImage(true);
      const res = await api.uploadVehicleImage(file);
      if (res.data && res.data.path) {
        setProfileVehicleImageUrls(prev => [...prev, res.data.path]);
        showToast('Vehicle image uploaded successfully. Click Save Profile Changes to persist.', 'success');
      }
    } catch (err: any) {
      console.error('Vehicle image upload failed:', err);
      showToast(err.response?.data?.message || 'Failed to upload vehicle image.', 'error');
    } finally {
      setIsUploadingVehicleImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteVehicleImage = (indexToRemove: number) => {
    setProfileVehicleImageUrls(prev => prev.filter((_, idx) => idx !== indexToRemove));
    showToast('Vehicle image removed. Click Save Profile Changes to persist.', 'info');
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingLogo(true);
      const response = await api.uploadLogo(file);
      if (response.data && response.data.path) {
        setLogoUrl(response.data.path);
        showToast('Logo uploaded successfully. Click Save Profile Changes to persist.', 'success');
      }
    } catch (err: any) {
      console.error('Logo upload failed:', err);
      showToast(err.response?.data?.message || 'Failed to upload logo.', 'error');
    } finally {
      setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleDeleteLogo = () => {
    setLogoUrl(null);
    showToast('Logo removed. Click Save Profile Changes to persist.', 'info');
  };

  const handleAddDistrict = () => {
    if (!selectedDistrict) return;
    if (!profileOutcodes.includes(selectedDistrict)) {
      setProfileOutcodes(prev => [...prev, selectedDistrict]);
    }
    setSelectedDistrict('');
  };

  const handleRemoveDistrict = (codeToRemove: string) => {
    setProfileOutcodes(prev => prev.filter(code => code !== codeToRemove));
  };

  const getOutcodeAreaName = (code: string) => {
    for (const group of REGION_GROUPS) {
      const found = group.outcodes.find(o => o.code === code);
      if (found) return `${found.area.split(',')[0]} (${group.name})`;
    }
    return code;
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    try {
      setSaving(true);
      setError(null);
      
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : null,
        bio: formData.bio,
        profileImageUrl: formData.profileImageUrl,
        // Instructor specific
        licenseNumber: formData.licenseNumber || null,
        licenseExpiryDate: formData.licenseExpiryDate ? new Date(formData.licenseExpiryDate).toISOString() : null,
        profileInfo: formData.profileInfo || null,
        baseHourlyRate: formData.baseHourlyRate ? parseFloat(formData.baseHourlyRate) : null,
        transmissionType: formData.transmissionType || null,
        licenseTypes: formData.licenseTypes || null,
        logoUrl: logoUrl,
        vehicleImageUrls: profileVehicleImageUrls,
        outcodes: profileOutcodes,
        // Student specific
        studentNumber: formData.studentNumber || null,
        theoryTestPassDate: formData.theoryTestPassDate ? new Date(formData.theoryTestPassDate).toISOString() : null,
        theoryTestPassReference: formData.theoryTestPassReference || null,
        dvlaShareCode: formData.dvlaShareCode || null,
        eyesightTestPassed: formData.eyesightTestPassed,
        emergencyContactName: formData.emergencyContactName || null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
        medicalConditions: formData.medicalConditions || null,
        licenseStatus: formData.licenseStatus || null,
        licenseRejectionReason: formData.licenseRejectionReason || null,
      };

      await api.adminUpdateUserDetails(userId, payload);
      showToast('User profile updated successfully!', 'success');
      await fetchUserDetails();
    } catch (err: any) {
      console.error('Error updating user profile:', err);
      setError(err.response?.data?.message || 'An error occurred while saving user details.');
      showToast('Failed to update user profile.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (newPassword.length < 6) {
      showToast('Password must be at least 6 characters long.', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match.', 'error');
      return;
    }

    try {
      setPasswordSaving(true);
      await api.adminChangePassword(userId, newPassword);
      showToast('Password changed successfully!', 'success');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('Error changing password:', err);
      showToast(err.response?.data?.message || 'Failed to change user password.', 'error');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const role = user?.role || 'Student';

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      {/* Back Link */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/dashboard')}
          variant="outlined"
          size="small"
          sx={{ fontWeight: 'bold' }}
        >
          Back to Dashboard
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Header Info Card */}
      <Card sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'primary.main', height: 10 }} />
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid>
              <Avatar
                src={api.getImageUrl(user?.profileImageUrl)}
                sx={{ width: 80, height: 80, fontSize: 32, bgcolor: role === 'Instructor' ? 'success.main' : 'primary.main' }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography variant="h5" fontWeight="bold">
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Chip
                  label={role}
                  color={role === 'Admin' ? 'secondary' : role === 'Instructor' ? 'success' : 'primary'}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
                <Chip
                  label={isActive ? 'Active Account' : 'Inactive'}
                  color={isActive ? 'success' : 'default'}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Email: <b>{user?.email}</b> | Registered: {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
              {details?.logoUrl && (
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Typography variant="body2" color="textSecondary">Company Logo:</Typography>
                  <img
                    src={api.getImageUrl(details.logoUrl)}
                    alt="Logo"
                    style={{ height: 30, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4, padding: 2 }}
                  />
                </Box>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: "auto" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={handleToggleStatus}
                    disabled={statusUpdating}
                    color="success"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="bold">
                    {statusUpdating ? 'Updating...' : isActive ? 'Active' : 'Locked'}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs Menu */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab label="Edit Profile Info" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Change Password" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="Payments & Invoices" icon={<PaymentIcon />} iconPosition="start" />
          {role === 'Student' && <Tab label="Lessons & Exams" icon={<SchoolIcon />} iconPosition="start" />}
          {role === 'Instructor' && <Tab label="Students & Expenses" icon={<PeopleIcon />} iconPosition="start" />}
        </Tabs>
      </Box>

      {/* Tab 0: Edit Profile Info */}
      {activeTab === 0 && (
        <form onSubmit={handleSaveProfile}>
          <Card sx={{ p: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom mb={3}>
                Edit Personal details
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={user?.email || ''}
                    disabled
                    helperText="Account email/username cannot be changed"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                  />
                </Grid>

                {/* Role Specific details */}
                {role === 'Instructor' && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }}><Chip label="Instructor Details" size="small" /></Divider>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="License Number"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="License Expiry Date"
                        name="licenseExpiryDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.licenseExpiryDate}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="Base Hourly Rate (£)"
                        name="baseHourlyRate"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        value={formData.baseHourlyRate}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel id="transmission-label">Transmission Type</InputLabel>
                        <Select
                          labelId="transmission-label"
                          label="Transmission Type"
                          value={formData.transmissionType}
                          onChange={(e) => handleSelectChange('transmissionType', e.target.value)}
                        >
                          <MenuItem value="Manual">Manual</MenuItem>
                          <MenuItem value="Automatic">Automatic</MenuItem>
                          <MenuItem value="Both">Both</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="License Categories (e.g. B, C)"
                        name="licenseTypes"
                        value={formData.licenseTypes}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Profile Info / Cover Description"
                        name="profileInfo"
                        value={formData.profileInfo}
                        onChange={handleInputChange}
                      />
                    </Grid>

                    {/* Logo Section */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Company Logo
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2}>
                        {logoUrl ? (
                          <Box sx={{ position: 'relative' }}>
                            <Avatar
                              src={api.getImageUrl(logoUrl)}
                              variant="rounded"
                              sx={{ width: 80, height: 80, border: '1px solid #ddd' }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={handleDeleteLogo}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: '#ffebee' },
                                boxShadow: 1
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              border: '1px dashed #ccc',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#fafafa'
                            }}
                          >
                            <Typography variant="caption" color="textSecondary">No Logo</Typography>
                          </Box>
                        )}
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          disabled={isUploadingLogo}
                          size="small"
                        >
                          {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleLogoUpload}
                          />
                        </Button>
                      </Box>
                    </Grid>

                    {/* Vehicle Photos */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Vehicle Photos (Max 3)
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                        {profileVehicleImageUrls.map((url, idx) => (
                          <Box key={idx} sx={{ position: 'relative' }}>
                            <Avatar
                              src={api.getImageUrl(url)}
                              variant="rounded"
                              sx={{ width: 80, height: 80, border: '1px solid #ddd' }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteVehicleImage(idx)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: '#ffebee' },
                                boxShadow: 1
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        {profileVehicleImageUrls.length < 3 && (
                          <Button
                            variant="outlined"
                            component="label"
                            sx={{
                              width: 80,
                              height: 80,
                              border: '1px dashed #ccc',
                              borderRadius: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textTransform: 'none',
                              p: 0,
                              bgcolor: '#fafafa'
                            }}
                            disabled={isUploadingVehicleImage}
                          >
                            <CloudUploadIcon color="action" />
                            <Typography variant="caption" sx={{ mt: 0.5 }}>Upload</Typography>
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleVehicleImageUpload}
                            />
                          </Button>
                        )}
                      </Box>
                    </Grid>

                    {/* Served Regions */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Served Areas / Regions
                      </Typography>
                      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="admin-city-label">Select City</InputLabel>
                            <Select
                              labelId="admin-city-label"
                              label="Select City"
                              value={selectedCity}
                              onChange={(e) => {
                                setSelectedCity(e.target.value);
                                setSelectedDistrict('');
                              }}
                            >
                              {REGION_GROUPS.map((group) => (
                                <MenuItem key={group.name} value={group.name}>
                                  {group.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <FormControl fullWidth size="small" disabled={!selectedCity}>
                            <InputLabel id="admin-district-label">Select District / Borough</InputLabel>
                            <Select
                              labelId="admin-district-label"
                              label="Select District / Borough"
                              value={selectedDistrict}
                              onChange={(e) => setSelectedDistrict(e.target.value)}
                            >
                              {(REGION_GROUPS.find((g) => g.name === selectedCity)?.outcodes || []).map((o) => (
                                <MenuItem key={o.code} value={o.code}>
                                  {o.area.split(',')[0]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleAddDistrict}
                            disabled={!selectedDistrict}
                            size="medium"
                            sx={{ fontWeight: 'bold' }}
                          >
                            Add
                          </Button>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                        {profileOutcodes.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">No regions added yet.</Typography>
                        ) : (
                          profileOutcodes.map((code) => (
                            <Chip
                              key={code}
                              label={getOutcodeAreaName(code)}
                              onDelete={() => handleRemoveDistrict(code)}
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 'bold' }}
                            />
                          ))
                        )}
                      </Box>
                    </Grid>
                  </>
                )}

                {role === 'Student' && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }}><Chip label="Student UK Legislation & License Details" size="small" /></Divider>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="Student ID Number"
                        name="studentNumber"
                        value={formData.studentNumber}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="License Number"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="License Expiry Date"
                        name="licenseExpiryDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.licenseExpiryDate}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="Theory Test Pass Date"
                        name="theoryTestPassDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.theoryTestPassDate}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="Theory Pass Reference"
                        name="theoryTestPassReference"
                        value={formData.theoryTestPassReference}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="DVLA Share Code"
                        name="dvlaShareCode"
                        value={formData.dvlaShareCode}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel id="student-transmission-label">Preferred Transmission</InputLabel>
                        <Select
                          labelId="student-transmission-label"
                          label="Preferred Transmission"
                          value={formData.transmissionType}
                          onChange={(e) => handleSelectChange('transmissionType', e.target.value)}
                        >
                          <MenuItem value="Manual">Manual</MenuItem>
                          <MenuItem value="Automatic">Automatic</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel id="license-status-label">DVLA License Verification</InputLabel>
                        <Select
                          labelId="license-status-label"
                          label="DVLA License Verification"
                          value={formData.licenseStatus}
                          onChange={(e) => handleSelectChange('licenseStatus', e.target.value)}
                        >
                          <MenuItem value="None">Not Uploaded (None)</MenuItem>
                          <MenuItem value="Pending">Pending Verification (Pending)</MenuItem>
                          <MenuItem value="Approved">Approved / Verified (Approved)</MenuItem>
                          <MenuItem value="Rejected">Rejected (Rejected)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.eyesightTestPassed}
                            onChange={(e) => handleCheckboxChange('eyesightTestPassed', e.target.checked)}
                            color="success"
                          />
                        }
                        label="Eyesight Test Passed (20.5m)"
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                    {formData.licenseStatus === 'Rejected' && (
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="License Rejection Reason"
                          name="licenseRejectionReason"
                          value={formData.licenseRejectionReason}
                          onChange={handleInputChange}
                          error={formData.licenseStatus === 'Rejected'}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Emergency Contact Name"
                        name="emergencyContactName"
                        value={formData.emergencyContactName}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Emergency Contact Phone"
                        name="emergencyContactPhone"
                        value={formData.emergencyContactPhone}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Medical Conditions / Special Needs"
                        name="medicalConditions"
                        value={formData.medicalConditions}
                        onChange={handleInputChange}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
            <Box display="flex" justifyContent="flex-end" p={2} sx={{ borderTop: '1px solid #eee' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{ fontWeight: 'bold' }}
              >
                {saving ? 'Saving...' : 'Save Profile Changes'}
              </Button>
            </Box>
          </Card>
        </form>
      )}

      {/* Tab 1: Change Password */}
      {activeTab === 1 && (
        <Card sx={{ p: 2, maxWidth: 500 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom mb={3}>
              Force Change Password
            </Typography>
            <form onSubmit={handleChangePassword}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={passwordSaving}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {passwordSaving ? 'Changing...' : 'Change Password'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Payments & Invoices */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom mb={3}>
            Payment History & Invoices
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Transaction ID</b></TableCell>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Method</b></TableCell>
                  <TableCell><b>Amount</b></TableCell>
                  <TableCell><b>Expiry Date</b></TableCell>
                  <TableCell><b>Status (Invoice)</b></TableCell>
                  <TableCell><b>Notes</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!details?.payments || details.payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No payments found for this user.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  details.payments.map((p: any) => (
                    <TableRow key={p.id} hover>
                      <TableCell><code>{p.transactionId || 'N/A'}</code></TableCell>
                      <TableCell>{new Date(p.paymentDate).toLocaleDateString()} {new Date(p.paymentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>
                        <Chip label={p.paymentMethod} size="small" variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell><b>£{p.amount.toFixed(2)}</b></TableCell>
                      <TableCell>{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.paymentStatus}
                          color={p.paymentStatus === 'Completed' ? 'success' : p.paymentStatus === 'Pending' ? 'warning' : 'error'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>{p.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 3: Lessons & Exams (Student Only) */}
      {activeTab === 3 && role === 'Student' && (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom mb={3}>
                Lesson History
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Date</b></TableCell>
                      <TableCell><b>Topic</b></TableCell>
                      <TableCell><b>Duration</b></TableCell>
                      <TableCell><b>Status</b></TableCell>
                      <TableCell><b>Notes</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!details?.lessons || details.lessons.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>No lessons scheduled.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      details.lessons.map((l: any) => (
                        <TableRow key={l.id} hover>
                          <TableCell>{new Date(l.lessonDate).toLocaleDateString()} {new Date(l.lessonDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                          <TableCell><b>{l.topicName || 'General Practice'}</b></TableCell>
                          <TableCell>{l.duration} mins</TableCell>
                          <TableCell>
                            {l.isCompleted ? (
                              <Chip label="Completed" color="success" size="small" icon={<CheckCircleIcon />} />
                            ) : l.isApproved ? (
                              <Chip label="Booked (Approved)" color="info" size="small" />
                            ) : (
                              <Chip label="Pending Approval" color="warning" size="small" />
                            )}
                          </TableCell>
                          <TableCell>{l.notes || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom mb={3}>
                Exams List
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Date</b></TableCell>
                      <TableCell><b>Exam Type</b></TableCell>
                      <TableCell><b>Result</b></TableCell>
                      <TableCell><b>Notes</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!details?.exams || details.exams.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>No exam records found.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      details.exams.map((e: any) => (
                        <TableRow key={e.id} hover>
                          <TableCell>{new Date(e.examDate).toLocaleDateString()}</TableCell>
                          <TableCell>{e.examType}</TableCell>
                          <TableCell>
                            <Chip
                              label={e.result}
                              color={e.result === 'Pass' ? 'success' : e.result === 'Fail' ? 'error' : 'default'}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell>{e.notes || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 3: Students & Expenses (Instructor Only) */}
      {activeTab === 3 && role === 'Instructor' && (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom mb={3} display="flex" alignItems="center" gap={1}>
                <PeopleIcon color="primary" /> Assigned Students
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Student Number</b></TableCell>
                      <TableCell><b>Name</b></TableCell>
                      <TableCell><b>Email</b></TableCell>
                      <TableCell align="right"><b>Action</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!details?.students || details.students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>No students assigned to this instructor.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      details.students.map((s: any) => (
                        <TableRow key={s.id} hover>
                          <TableCell><code>{s.studentNumber || 'N/A'}</code></TableCell>
                          <TableCell><b>{s.firstName} {s.lastName}</b></TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                // Navigate to this student's management page
                                navigate(`/admin/users/${s.id}`);
                                setActiveTab(0);
                              }}
                            >
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom mb={3} display="flex" alignItems="center" gap={1}>
                <ReceiptIcon color="success" /> Logged Expenses
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Date</b></TableCell>
                      <TableCell><b>Category</b></TableCell>
                      <TableCell><b>Amount</b></TableCell>
                      <TableCell><b>Description</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!details?.expenses || details.expenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>No expenses logged by this instructor.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      details.expenses.map((exp: any) => (
                        <TableRow key={exp.id} hover>
                          <TableCell>{new Date(exp.expenseDate).toLocaleDateString()}</TableCell>
                          <TableCell><Chip label={exp.category} size="small" variant="outlined" /></TableCell>
                          <TableCell><b>£{exp.amount.toFixed(2)}</b></TableCell>
                          <TableCell>{exp.description || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AdminUserEdit;
